import { supabase } from '../lib/supabase';

export interface VideoTag {
  id: string;
  match_id: string;
  video_id: string;
  source_log_id: string | null;
  team_id: string | null;
  player_id: string | null;
  stat_type_id: string | null;
  video_time_seconds: number;
  match_minute: number | null;
  tags: string[];
  created_at: string;
  updated_at: string;
  is_active: boolean;
  // Related data (from joins)
  team?: { id: string; name: string } | null;
  player?: { id: string; full_name: string } | null;
  stat_type?: { id: string; stat_key: string; display_name: string } | null;
  video?: { id: string; video_url: string; video_order: number } | null;
}

export const videoTagsService = {
  /**
   * Get all video tags for a match
   * @param matchId - Match ID
   * @param videoId - Optional video ID to filter by specific video
   * @returns Array of video tags
   */
  async getVideoTags(matchId: string, videoId?: string): Promise<{ success: boolean; data?: VideoTag[]; error?: string }> {
    try {
      let query = supabase
        .from('match_video_tags')
        .select(`
          id,
          match_id,
          video_id,
          source_log_id,
          team_id,
          player_id,
          stat_type_id,
          video_time_seconds,
          match_minute,
          tags,
          created_at,
          updated_at,
          is_active,
          teams:match_video_tags_team_id_fkey(id, name),
          players:match_video_tags_player_id_fkey(id, full_name),
          stat_types:match_video_tags_stat_type_id_fkey(id, stat_key, display_name),
          match_videos:match_video_tags_video_id_fkey(id, video_url, video_order)
        `)
        .eq('match_id', matchId)
        .eq('is_active', true)
        .order('video_time_seconds', { ascending: true });

      if (videoId) {
        query = query.eq('video_id', videoId);
      }

      const { data, error } = await query;

      if (error) {
        throw new Error(`Failed to fetch video tags: ${error.message}`);
      }

      // Transform the data to match VideoTag interface
      const transformedData: VideoTag[] = (data || []).map((tag: any) => ({
        id: tag.id,
        match_id: tag.match_id,
        video_id: tag.video_id,
        source_log_id: tag.source_log_id,
        team_id: tag.team_id,
        player_id: tag.player_id,
        stat_type_id: tag.stat_type_id,
        video_time_seconds: parseFloat(tag.video_time_seconds),
        match_minute: tag.match_minute,
        tags: Array.isArray(tag.tags) ? tag.tags : [],
        created_at: tag.created_at,
        updated_at: tag.updated_at,
        is_active: tag.is_active,
        team: tag.teams ? { id: tag.teams.id, name: tag.teams.name } : null,
        player: tag.players ? { id: tag.players.id, full_name: tag.players.full_name } : null,
        stat_type: tag.stat_types ? { id: tag.stat_types.id, stat_key: tag.stat_types.stat_key, display_name: tag.stat_types.display_name } : null,
        video: tag.match_videos ? { id: tag.match_videos.id, video_url: tag.match_videos.video_url, video_order: tag.match_videos.video_order } : null,
      }));

      return { success: true, data: transformedData };
    } catch (error) {
      console.error('Error fetching video tags:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  },

  /**
   * Get key events with video timing for timeline overlay
   * Joins key_match_events with match_video_tags via source_log_id
   * @param matchId - Match ID
   * @param videoId - Optional video ID to filter by specific video
   * @returns Array of key events with video timing information
   */
  async getKeyEventsWithVideoTiming(
    matchId: string, 
    videoId?: string
  ): Promise<{ 
    success: boolean; 
    data?: Array<{
      eventId: string;
      matchMinute: number | null;
      eventTimestamp: string;
      statType: { id: string; stat_key: string; display_name: string } | null;
      team: { id: string; name: string } | null;
      primaryPlayer: { id: string; full_name: string } | null;
      videoTimeSeconds: number | null;
      videoId: string | null;
      tags: string[];
    }>; 
    error?: string 
  }> {
    try {
      // Get key events for the match
      const { KeyEventsService } = await import('./keyEventsService');
      const keyEvents = await KeyEventsService.getKeyEvents({ 
        match_id: matchId,
        status: 'published' // Only published events for timeline
      });

      if (keyEvents.length === 0) {
        return { success: true, data: [] };
      }

      // Get source_log_ids from key events
      const sourceLogIds = keyEvents
        .map(event => event.source_log_id)
        .filter((id): id is string => id !== null && id !== undefined);

      if (sourceLogIds.length === 0) {
        // Return events without video timing
        return { 
          success: true, 
          data: keyEvents.map(event => ({
            eventId: event.id,
            matchMinute: event.match_minute,
            eventTimestamp: event.event_timestamp || new Date().toISOString(),
            statType: event.stat_type ? {
              id: event.stat_type.id,
              stat_key: event.stat_type.stat_key,
              display_name: event.stat_type.display_name
            } : null,
            team: event.team ? { id: event.team.id, name: event.team.name } : null,
            primaryPlayer: event.primary_player ? {
              id: event.primary_player.id,
              full_name: event.primary_player.full_name
            } : null,
            videoTimeSeconds: null,
            videoId: null,
            tags: Array.isArray(event.tags) ? event.tags : []
          }))
        };
      }

      // Get video tags for these source_log_ids (outer join - include events without video tags)
      let videoTagsQuery = supabase
        .from('match_video_tags')
        .select(`
          id,
          video_id,
          source_log_id,
          video_time_seconds,
          tags,
          match_videos:match_video_tags_video_id_fkey(id, video_url, video_order)
        `)
        .in('source_log_id', sourceLogIds)
        .eq('is_active', true);

      if (videoId) {
        videoTagsQuery = videoTagsQuery.eq('video_id', videoId);
      }

      const { data: videoTags, error: tagsError } = await videoTagsQuery;

      if (tagsError) {
        throw new Error(`Failed to fetch video tags: ${tagsError.message}`);
      }

      // Create a map: source_log_id -> videoTag (outer join - events without tags will have null)
      const videoTagsMap = new Map<string, any>();
      (videoTags || []).forEach((tag: any) => {
        if (tag.source_log_id) {
          videoTagsMap.set(tag.source_log_id, {
            videoTimeSeconds: parseFloat(tag.video_time_seconds),
            videoId: tag.video_id,
            tags: Array.isArray(tag.tags) ? tag.tags : [],
            video: tag.match_videos
          });
        }
      });

      // Combine key events with their video tags
      const result = keyEvents.map(event => {
        const videoTag = event.source_log_id ? videoTagsMap.get(event.source_log_id) : null;
        
        return {
          eventId: event.id,
          matchMinute: event.match_minute,
          eventTimestamp: event.event_timestamp || new Date().toISOString(),
          statType: event.stat_type ? {
            id: event.stat_type.id,
            stat_key: event.stat_type.stat_key,
            display_name: event.stat_type.display_name
          } : null,
          team: event.team ? { id: event.team.id, name: event.team.name } : null,
          primaryPlayer: event.primary_player ? {
            id: event.primary_player.id,
            full_name: event.primary_player.full_name
          } : null,
          videoTimeSeconds: videoTag?.videoTimeSeconds ?? null,
          videoId: videoTag?.videoId ?? null,
          tags: Array.isArray(videoTag?.tags) ? videoTag.tags : (Array.isArray(event.tags) ? event.tags : [])
        };
      });

      return { success: true, data: result };
    } catch (error) {
      console.error('Error fetching key events with video timing:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  },
};

